import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  Message,
  WelcomeElement,
} from '@components';
import { authorsQuery, siteQuery } from '@queries';
import { ChevronRightIcon, SearchIcon } from '@svgs';

import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';

import AuthorRow from './AuthorRow/AuthorRow.jsx';

const matchAuthor = (author, query) => {
  // determine filter match for a particular Author

  // explicit matching only
  const exactMatchFields = [author.id.toString()];
  if (exactMatchFields.some((v) => v && v.toLowerCase() === query)) {
    return true;
  }

  // string match for fields
  const authorFields = [author.name];
  if (authorFields.some((v) => v && v.toLowerCase().indexOf(query) > -1)) {
    return true;
  }

  return false;
};

const filterAuthors = (authors, query) => {
  // client side filtering of authors
  const cleanedQuery = query.toLowerCase().trim();
  return authors.filter((author) => {
    if (matchAuthor(author, cleanedQuery)) {
      return author;
    }
  });
};

export const Authors = () => {
  const { t, i18n } = useTranslation();
  const { siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // filter search term
  const [searchTerm, setSearchTerm] = useState('');

  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  const {
    data: authorsData,
    isPending: authorsIsPending,
    isError,
  } = useQuery(
    authorsQuery({
      limit: 100,
      order_by: 'name',
      site_id: siteID,
    }),
  );

  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // https://medium.com/nerd-for-tech/debounce-your-search-react-input-optimization-fd270a8042b
  const debouncedResults = useMemo(() => {
    return debounce(handleFilterChange, 300);
  }, []);
  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  // filter websites if there is a search term, otherwise return the full list
  const filteredAuthors = searchTerm
    ? filterAuthors(authorsData.objects, searchTerm)
    : authorsData?.objects;

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <Link to={`/dashboard/sites/${siteID}/blogs`}>Blogs</Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              <span>{t('Blog authors')}</span>
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            <div className="sb-manage-content-head">
              <h1>{t('Blog authors')}</h1>
              <div className="sb-button-list-inline">
                <Link
                  to={`/dashboard/sites/${siteID}/blogs/authors/new`}
                  className="sb-btn sb-btn-cancel"
                >
                  Add new
                </Link>
              </div>
            </div>
            <div className="sb-manage-options-menu sb-flex">
              <div className="sb-manage-options-left">
                <div className="sb-flex">
                  <form
                    role="search"
                    className="sb-manage-options-search"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      type="search"
                      name="query"
                      placeholder="Filter"
                      aria-label="Enter search term"
                      autoComplete="off"
                      defaultValue={searchTerm}
                      onChange={debouncedResults}
                      disabled={authorsIsPending}
                    />
                    <SearchIcon />
                  </form>
                </div>
              </div>
              <div className="sb-manage-options-right"></div>
            </div>
            {authorsIsPending && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {!authorsIsPending &&
              (isError || authorsData.objects?.length == 0) && (
                <WelcomeElement>
                  <p>No blog authors under this website</p>
                  <Link
                    to={`/dashboard/sites/${siteID}/blogs/authors/new`}
                    className="sb-btn sb-btn-save"
                  >
                    Add first author
                  </Link>
                </WelcomeElement>
              )}
            {!authorsIsPending && !isError && (
              <div className="sb-content-view-row">
                {filteredAuthors.map((author) => {
                  return <AuthorRow key={author.id} author={author} />;
                })}
              </div>
            )}
            {!authorsIsPending && searchTerm && (
              <Message
                messageStyle="notice"
                title={
                  `Filtered to ${filteredAuthors.length} of ${authorsData.objects.length} author` +
                  (authorsData.objects.length != 1 ? 's' : '')
                }
                fadeoutMessage={false}
                isFormMessage={false}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
